/**
 * Cloudflare-only error monitoring for privacy-compliant analytics
 *
 * This implementation uses Cloudflare Analytics Engine for anonymous
 * error tracking without any PII or external tracking services.
 */

import {
  ErrorSeverity,
  ErrorCategory,
  type ErrorContext,
} from './error-handling';
import { ErrorSanitizer } from './error-sanitizer';

interface CloudflareErrorEvent {
  event: string;
  timestamp: number;
  severity: string;
  category: string;
  errorHash: string;
  path?: string;
  browser?: string;
  os?: string;
  dimensions?: Record<string, string>;
}

export class CloudflareErrorMonitor {
  private readonly sanitizer = new ErrorSanitizer();
  private readonly endpoint = '/api/analytics/errors';
  private errorBuffer: CloudflareErrorEvent[] = [];
  private readonly maxBufferSize = 10;
  private flushTimeout: NodeJS.Timeout | null = null;

  /**
   * Initialize the Cloudflare error monitor
   */
  initialize() {
    // Only initialize if we're in production
    if (import.meta.env.PROD) {
      // Flush errors before page unload
      window.addEventListener('beforeunload', () => {
        this.flush();
      });

      // Periodic flush every 30 seconds
      setInterval(() => {
        if (this.errorBuffer.length > 0) {
          this.flush();
        }
      }, 30000);
    }
  }

  /**
   * Report an error to Cloudflare Analytics Engine
   */
  reportError(error: Error, context?: ErrorContext) {
    // Only report in production
    if (!import.meta.env.PROD) {
      return;
    }

    // Sanitize the error to remove any PII
    const sanitizedError = this.sanitizer.sanitizeError(error);
    const sanitizedContext = context
      ? this.sanitizer.sanitizeContext(context)
      : undefined;

    // Create anonymous error event
    const event: CloudflareErrorEvent = {
      event: 'error',
      timestamp: Date.now(),
      severity: sanitizedContext?.severity || ErrorSeverity.MEDIUM,
      category: sanitizedContext?.category || ErrorCategory.SYSTEM,
      errorHash: this.hashError(sanitizedError),
      path: this.getCurrentPath(),
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      dimensions: this.buildDimensions(sanitizedContext),
    };

    // Add to buffer
    this.errorBuffer.push(event);

    // Flush if buffer is full
    if (this.errorBuffer.length >= this.maxBufferSize) {
      this.flush();
    } else {
      // Schedule a flush
      this.scheduleFlush();
    }
  }

  /**
   * Flush error buffer to Cloudflare
   */
  private async flush() {
    if (this.errorBuffer.length === 0) {
      return;
    }

    const events = [...this.errorBuffer];
    this.errorBuffer = [];

    try {
      // Send to Cloudflare Analytics Engine via our API
      // This endpoint should be configured to forward to Analytics Engine
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
        // Use keepalive for beforeunload scenarios
        keepalive: true,
      });
    } catch (error) {
      // Failed to send - don't retry to avoid infinite loops
      console.warn('Failed to send error analytics');
    }

    // Clear any pending flush
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
  }

  /**
   * Schedule a flush in the near future
   */
  private scheduleFlush() {
    if (this.flushTimeout) {
      return;
    }

    this.flushTimeout = setTimeout(() => {
      this.flush();
    }, 5000);
  }

  /**
   * Generate a hash for error deduplication
   */
  private hashError(error: Error): string {
    const message = error.message || 'Unknown error';
    const stack = error.stack?.split('\n')[0] || '';
    const combined = `${message}|${stack}`;

    // Simple hash function for deduplication
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Get current page path (sanitized)
   */
  private getCurrentPath(): string {
    const path = window.location.pathname;
    // Remove any potential IDs or sensitive data from path
    return path.replace(/\/[a-f0-9-]{36}/gi, '/:id').replace(/\/\d+/g, '/:num');
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  }

  /**
   * Get OS information
   */
  private getOSInfo(): string {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes('win')) return 'Windows';
    if (platform.includes('mac')) return 'macOS';
    if (platform.includes('linux')) return 'Linux';
    if (platform.includes('iphone') || platform.includes('ipad')) return 'iOS';
    if (platform.includes('android')) return 'Android';
    return 'Other';
  }

  /**
   * Build anonymous dimensions for analytics
   */
  private buildDimensions(context?: ErrorContext): Record<string, string> {
    const dimensions: Record<string, string> = {};

    // Add anonymous dimensions only
    if (context?.severity) {
      dimensions.severity = context.severity;
    }

    if (context?.category) {
      dimensions.category = context.category;
    }

    // Add feature flags or experiment info if available
    const featureFlags = this.getFeatureFlags();
    if (featureFlags) {
      dimensions.features = featureFlags;
    }

    return dimensions;
  }

  /**
   * Get active feature flags (example)
   */
  private getFeatureFlags(): string | undefined {
    // This would check for active feature flags
    // For now, return undefined
    return undefined;
  }

  /**
   * Get anonymous usage statistics
   */
  getStatistics() {
    return {
      bufferedErrors: this.errorBuffer.length,
      isInitialized: import.meta.env.PROD,
    };
  }
}

// Singleton instance
export const cloudflareErrorMonitor = new CloudflareErrorMonitor();
